"use client";
import { useTranslations } from "@zapier/i18n";

import { useState } from "react";

import {
  AlertDescription,
  Button,
  Link,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
} from "@zapier/design-system";

import {
  useAddProjectAsset,
  useDuplicateZap,
  type PrivateFolderOwnerMismatch,
} from "@zapier/client";
import { useNotification, getBuildAssetUrl, AssetTypes } from "@zapier/utils";
import { StorageZap } from "@zapier/bff";
import { useRouter } from "next/navigation";
import { ZapModal } from "../types";
import { TransferOwnershipPrompt } from "../../folders/MoveModals/TransferOwnershipPrompt";
import {
  useEmitZapKebabInteractionEvent,
  ZapKebabInteractionOption,
} from "../../../events";

interface Props {
  currentItemId?: number | string;
  currentItemTitle: string;
  /** Ask before the copy would be handed to the owner of someone else's private folder. */
  confirmsOwnerTransfer?: boolean;
  isOpen: boolean;
  projectId?: string;
  setIsOpen: (isOpen: boolean) => void;
  setCurrentModal: (modal?: ZapModal | undefined) => void;
  folderId?: string; // used for cache invalidation
}

interface ContentProps extends Props {
  onShowErrorNotification: (errorMessage: string) => void;
  onShowSuccessNotification: (zap: Partial<StorageZap>) => void;
}

function ZapDuplicateModalContent({
  currentItemTitle,
  confirmsOwnerTransfer = false,
  isOpen,
  setIsOpen,
  currentItemId,
  projectId,
  onShowErrorNotification,
  onShowSuccessNotification,
  folderId,
}: ContentProps) {
  const t = useTranslations("zap-duplicate-modal");

  const router = useRouter();

  const { emit: emitZapEvent } = useEmitZapKebabInteractionEvent();

  const [willEditAfterDuplicating, setWillEditAfterDuplicating] =
    useState<boolean>(false);
  const [pendingOwnerTransfer, setPendingOwnerTransfer] = useState<
    PrivateFolderOwnerMismatch | undefined
  >();
  // The prompt stays mounted through the editor redirect after a confirmed
  // "Duplicate and edit", so confirm must run once per prompt.
  const [hasConfirmedTransfer, setHasConfirmedTransfer] = useState(false);

  const handleError = (errorMessage: string) => {
    setIsOpen(false);
    setWillEditAfterDuplicating(false);
    onShowErrorNotification(errorMessage);
  };

  const { addProjectAsset } = useAddProjectAsset({
    projectId,
  });

  const handleSuccess = (zap: Partial<StorageZap>) => {
    // if the zap being duplicated is in a project (i.e. has a project ID),
    //add the new zap to the project after its successful creation
    if (projectId && zap?.id) {
      addProjectAsset({ type: AssetTypes.Zap, projectId, assetId: zap.id });
    }

    if (willEditAfterDuplicating && zap?.id) {
      router.push(getBuildAssetUrl(AssetTypes.Zap, zap?.id));
    } else {
      setIsOpen(false);
      onShowSuccessNotification(zap);
    }
  };

  const { duplicateZap, isDuplicating } = useDuplicateZap({
    onSuccess: handleSuccess,
    onError: handleError,
    onPrivateFolderOwnerMismatch: confirmsOwnerTransfer
      ? setPendingOwnerTransfer
      : undefined,
    folderId,
  });

  const handleDuplicate = async (willEdit: boolean) => {
    emitZapEvent(ZapKebabInteractionOption.Duplicate);
    if (!currentItemId) {
      return;
    }
    setWillEditAfterDuplicating(willEdit);

    duplicateZap({
      zapId: String(currentItemId),
      ...(confirmsOwnerTransfer ? { privateFolderOwnerPolicy: "refuse" } : {}),
    });
  };

  const confirmOwnerTransfer = () => {
    if (!currentItemId || hasConfirmedTransfer) {
      return;
    }
    setHasConfirmedTransfer(true);
    duplicateZap({
      zapId: String(currentItemId),
      privateFolderOwnerPolicy: "transfer",
    });
  };

  const cancelOwnerTransfer = () => {
    setPendingOwnerTransfer(undefined);
    setWillEditAfterDuplicating(false);
  };

  if (pendingOwnerTransfer) {
    return (
      <TransferOwnershipPrompt
        action="duplicate"
        folderOwnerId={pendingOwnerTransfer.folderOwnerId}
        assetCount={pendingOwnerTransfer.assets.length}
        isPending={isDuplicating || hasConfirmedTransfer}
        onCancel={cancelOwnerTransfer}
        onConfirm={confirmOwnerTransfer}
      />
    );
  }
  // We never re-enable the button for "Duplicate and edit" operations, but leave the loading state and modal in place until the redirect occurs.
  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={() => handleDuplicate(true)}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            if (isDuplicating || willEditAfterDuplicating) return;
            return event.key === "Enter" && handleDuplicate(true);
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("header.title", { currentItemTitle: currentItemTitle })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          {t("body.description", { currentItemTitle: currentItemTitle })}
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button
            variant="tertiary"
            onPress={() => setIsOpen(false)}
            isDisabled={isDuplicating || willEditAfterDuplicating}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="secondary"
            onPress={() => handleDuplicate(false)}
            isPending={isDuplicating && !willEditAfterDuplicating}
            isDisabled={isDuplicating || willEditAfterDuplicating}
          >
            {t("buttons.duplicate")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            onPress={() => handleDuplicate(true)}
            isPending={willEditAfterDuplicating}
            isDisabled={isDuplicating || willEditAfterDuplicating}
          >
            {t("buttons.duplicate-and-edit")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}
export const ZapDuplicateModal = ({
  currentItemId,
  currentItemTitle,
  confirmsOwnerTransfer,
  isOpen,
  projectId,
  setCurrentModal,
  setIsOpen,
  folderId,
}: Props) => {
  const t = useTranslations("zap-duplicate-modal");

  const { createNotification } = useNotification();

  const onShowSuccessNotification = (zap: Partial<StorageZap>) => {
    if (zap.id) {
      createNotification({
        message: (
          <AlertDescription>
            {t.rich("notifications.success-message", {
              zapTitle: zap.title || "",
              editorLink: (chunks) => (
                <Link
                  href={getBuildAssetUrl(AssetTypes.Zap, zap.id!)}
                  target="_blank"
                >
                  {chunks}
                </Link>
              ),
            })}
          </AlertDescription>
        ),
        type: "success",
      });
    }
  };

  const onShowErrorNotification = (errorMessage: string) => {
    createNotification({
      title: `Unable to duplicate "${currentItemTitle}"`,
      message: errorMessage,
      type: "error",
    });
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <ZapDuplicateModalContent
      currentItemId={currentItemId}
      currentItemTitle={currentItemTitle}
      confirmsOwnerTransfer={confirmsOwnerTransfer}
      isOpen={isOpen}
      projectId={projectId}
      setCurrentModal={setCurrentModal}
      setIsOpen={setIsOpen}
      onShowErrorNotification={onShowErrorNotification}
      onShowSuccessNotification={onShowSuccessNotification}
      folderId={folderId}
    />
  );
};
